import { motion } from "framer-motion";
import { ShieldCheck, Award, Users, HardHat } from "lucide-react";

/**
 * CrewSafetyFirst — vertical/portrait photo showcase of a Bespoke crew
 * member at height in full fall-arrest safety gear.
 *
 * Reinforces: our people are trained, insured, and protected — the same
 * discipline standard our founder brought back from the Army.
 *
 * Layout: photo LEFT (portrait, 4:5), copy RIGHT with 4 field-standard
 * metrics presented like a military spec sheet.
 */
export default function CrewSafetyFirst() {
  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ background: "var(--bg)" }}
      data-testid="crew-safety-first"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(1000px 500px at 20% 30%, rgba(198,168,124,0.06), transparent 60%)",
      }} />

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* PHOTO — portrait, occupies 5 cols on left */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div
              className="relative overflow-hidden"
              style={{
                aspectRatio: "3 / 4",
                borderRadius: "28px",
                border: "1px solid var(--line-gold)",
                boxShadow: "0 50px 100px -30px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.03) inset",
              }}
            >
              <img
                src="/brand/crew/safety-harness-roof.webp"
                alt="Bespoke crew member operating at height in fall-arrest harness"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "brightness(0.92) contrast(1.05) saturate(0.98)" }}
                loading="lazy"
              />
              {/* Vignette */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 100%)",
              }} />
              {/* HUD corner marks */}
              {["tl", "tr", "bl", "br"].map((c) => {
                const isTop = c.startsWith("t"), isLeft = c.endsWith("l");
                return (
                  <div key={c} className="absolute pointer-events-none" style={{
                    [isTop ? "top" : "bottom"]: "18px",
                    [isLeft ? "left" : "right"]: "18px",
                    width: "24px", height: "24px",
                    borderTop: isTop ? "1px solid var(--gold)" : "none",
                    borderBottom: !isTop ? "1px solid var(--gold)" : "none",
                    borderLeft: isLeft ? "1px solid var(--gold)" : "none",
                    borderRight: !isLeft ? "1px solid var(--gold)" : "none",
                    opacity: 0.6,
                  }} />
                );
              })}
              {/* Bottom badge */}
              <div className="absolute bottom-6 left-6 flex items-center gap-2 px-3 py-1.5" style={{
                background: "rgba(0,0,0,0.55)",
                border: "1px solid var(--line-gold)",
                borderRadius: "999px",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
              }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ade80" }} />
                <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold-2)" }}>
                  Crew · At-height
                </span>
              </div>
            </div>
          </motion.div>

          {/* COPY — 7 cols on right */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.0, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="overline mb-4">The Field Standard</div>
            <h2 className="font-display" style={{ fontSize: "clamp(2.4rem, 5vw, 4.4rem)", letterSpacing: "-0.035em", lineHeight: 1 }}>
              We train the crew like{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>the mission depended on it.</span>
            </h2>
            <p className="mt-8 font-body max-w-xl" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.65 }}>
              Every technician who steps onto your property is fall-arrest certified, OSHA-30 credentialed, and drilled monthly on the discipline that took our founder through six years in uniform. We don't send subcontractors. We don't send day labor. We send Bespoke.
            </p>

            {/* Spec-sheet grid */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { Icon: ShieldCheck, k: "OSHA-30", v: "Certified" },
                { Icon: HardHat,     k: "Fall-arrest", v: "Rated" },
                { Icon: Users,       k: "Direct hire", v: "0 subcontract" },
                { Icon: Award,       k: "Monthly", v: "Recertification" },
              ].map((s, i) => (
                <div key={i} className="p-4" style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "14px",
                }}>
                  <s.Icon size={14} style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                  <div className="mt-3 font-display text-xl" style={{ letterSpacing: "-0.02em", color: "var(--gold-2)" }}>
                    {s.k}
                  </div>
                  <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
                    {s.v}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em]" style={{
              borderTop: "1px solid var(--line)", color: "var(--text-3)",
            }}>
              <div className="w-8 h-px" style={{ background: "var(--line-gold)" }} />
              This is not a stock photo · This is our team, on your building
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
